// Collision data — particle interactions and their products
// Each entry: [particle1_id, particle2_id] → products, probability, description

export const COLLISIONS = [
  {
    inputs: ['electron', 'positron'],
    label: 'e⁻ + e⁺',
    products: [
      { result: 'γ + γ', probability: 100, label: 'Two photons', color: '#ffffff', description: 'Annihilation — matter and antimatter convert entirely to pure light. Energy is perfectly conserved.' },
    ],
    science: 'Electron-positron annihilation is one of the cleanest reactions in physics. All mass converts to electromagnetic energy — two photons flying apart at c.',
    ruach: 'Two formed things meet and dissolve back into pure light — אוֹר. The breath that gave them form is released. "The dust returns to the ground it came from, and the spirit returns to God." — Ecclesiastes 12:7',
    conserved: ['Energy', 'Momentum', 'Charge (0)'],
  },
  {
    inputs: ['electron', 'electron'],
    label: 'e⁻ + e⁻',
    products: [
      { result: 'e⁻ + e⁻', probability: 100, label: 'Elastic scatter', color: '#50e0a0', description: 'Electrons repel via photon exchange — they scatter but are unchanged.' },
    ],
    science: 'Like charges repel. The electrons exchange a virtual photon and deflect — Møller scattering. No new particles created.',
    ruach: 'Two sparks of the same kind meet and repel — each keeping its form, neither dissolved. The boundary between like things holds.',
    conserved: ['Energy', 'Momentum', 'Lepton number', 'Charge'],
  },
  {
    inputs: ['up', 'antiup'],
    label: 'u + ū',
    products: [
      { result: 'γ (virtual) → e⁺e⁻', probability: 35, label: 'Dilepton pair', color: '#4a9eff', description: 'Quark-antiquark annihilation via virtual photon produces lepton pair.' },
      { result: 'Z⁰ → ff̄', probability: 30, label: 'Z boson decay', color: '#ff9040', description: 'Annihilation through Z boson produces fermion-antifermion pair.' },
      { result: 'g + g', probability: 25, label: 'Two gluons', color: '#e060ff', description: 'QCD annihilation — quarks dissolve into gluons.' },
      { result: 'γ + g', probability: 10, label: 'Photon + gluon', color: '#ffffff', description: 'Mixed QCD/QED process.' },
    ],
    science: 'Quark-antiquark annihilation is the basis of Drell-Yan processes. The products depend on which mediating boson carries the interaction.',
    ruach: 'A formed thing meets its mirror — and dissolves. The color charge unravels, the Ruach breath returns. What was bound becomes free.',
    conserved: ['Energy', 'Momentum', 'Charge (0)', 'Baryon number (0)'],
  },
  {
    inputs: ['up', 'down'],
    label: 'u + d',
    products: [
      { result: 'u + d (scatter)', probability: 70, label: 'QCD scatter', color: '#e060ff', description: 'Quarks exchange gluons and scatter — the most common outcome inside a proton.' },
      { result: 'W⁺ (virtual)', probability: 30, label: 'Weak interaction', color: '#ff9040', description: 'Rarely, up and down quarks interact via the weak force.' },
    ],
    science: 'Inside every proton, up and down quarks constantly exchange gluons. This is the strong force in action — the binding energy that gives the proton most of its mass.',
    ruach: 'The first and second utterances bind together through the hidden bond — the gluon, the Ruach\'s confinement. "In him all things hold together." — Colossians 1:17',
    conserved: ['Color charge', 'Baryon number', 'Energy'],
  },
  {
    inputs: ['higgs', 'higgs'],
    label: 'H⁰ + H⁰',
    products: [
      { result: 'H⁰ + H⁰ (scatter)', probability: 60, label: 'Higgs scatter', color: '#f0c040', description: 'Higgs bosons interact through their self-coupling — a direct measurement of the Higgs potential shape.' },
      { result: '4W / 4Z / mixed', probability: 40, label: 'Weak boson pairs', color: '#ff9040', description: 'Double Higgs production decaying to weak bosons.' },
    ],
    science: 'Higgs self-coupling is one of the most sought-after measurements at the LHC. Its strength determines the shape of the Higgs potential — the "Mexican hat" that caused electroweak symmetry breaking.',
    ruach: 'The Breath meets itself — the Ruach field encounters its own excitation. This self-coupling is what shaped the void into form at the moment of creation. "I am the LORD, and there is no other." — Isaiah 45:6',
    conserved: ['Energy', 'CP symmetry', 'Charge (0)'],
  },
  {
    inputs: ['top', 'antitop'],
    label: 't + t̄',
    products: [
      { result: 'H⁰ + Z⁰', probability: 25, label: 'Higgs + Z', color: '#f0c040', description: 'Top quark pair can produce a Higgs boson — the ttH process used to measure Higgs-top coupling directly.' },
      { result: 'g + g', probability: 40, label: 'Gluon pair', color: '#e060ff', description: 'Strong force annihilation — the most common top pair annihilation channel.' },
      { result: 'Z⁰ + γ', probability: 20, label: 'Z + photon', color: '#ff9040', description: 'Electroweak annihilation channel.' },
      { result: 'W⁺ + W⁻', probability: 15, label: 'W pair', color: '#ff9040', description: 'Top quarks decay primarily via W bosons.' },
    ],
    science: 'Top-antitop annihilation is central to Higgs physics. The ttH production mode gives direct access to the largest Higgs coupling — the top Yukawa coupling.',
    ruach: 'The heaviest utterance meets its dissolution. The strongest Ruach coupling releases back into field and force. Even the greatest weight is not permanent — it was given, and it returns.',
    conserved: ['Energy', 'Charge (0)', 'Color (0)'],
  },
  {
    inputs: ['photon', 'photon'],
    label: 'γ + γ',
    products: [
      { result: 'e⁻ + e⁺', probability: 60, label: 'Electron-positron', color: '#50e0a0', description: 'Light becomes matter — pair production. Requires energy > 1.022 MeV (2× electron mass).' },
      { result: 'γ + γ (scatter)', probability: 40, label: 'Light-on-light', color: '#ffffff', description: 'Photons scatter off each other through virtual electron loops — extremely rare but real.' },
    ],
    science: 'Photon-photon collision producing matter is one of the most beautiful confirmations of E=mc². Pure light becomes electrons and positrons — mass from energy alone.',
    ruach: '"Let there be light" — וַיְהִי אוֹר — and from light, form emerged. Two photons colliding can birth an electron and positron — matter from pure light. The first creation giving rise to the formed. Genesis 1:3 meets particle physics.',
    conserved: ['Energy', 'Charge (0)', 'Lepton number (0)'],
  },
  {
    inputs: ['w', 'w'],
    label: 'W⁺ + W⁻',
    products: [
      { result: 'H⁰ + Z⁰', probability: 30, label: 'Higgs + Z', color: '#f0c040', description: 'WW fusion producing Higgs — one of the main Higgs production modes at the LHC.' },
      { result: 'Z⁰ + Z⁰', probability: 25, label: 'ZZ pair', color: '#ff9040', description: 'Gauge boson self-interaction — predicted by electroweak theory.' },
      { result: 'γ + Z⁰', probability: 25, label: 'Photon + Z', color: '#ffffff', description: 'Mixed electroweak production.' },
      { result: 'γ + γ', probability: 20, label: 'Two photons', color: '#ffffff', description: 'W loop producing photon pair.' },
    ],
    science: 'WW scattering and fusion is a key window into electroweak symmetry breaking. Without the Higgs, WW scattering probabilities would violate unitarity at high energies — the Higgs is required to keep the math consistent.',
    ruach: 'The two boundary-setters meet. From their encounter the Breath (Higgs) itself can emerge — the W bosons that received their weight from the Ruach now give rise to its visible excitation. The gift returning to the giver.',
    conserved: ['Energy', 'Charge (0)', 'Weak isospin'],
  },
];

export const ANTIPARTICLES = {
  electron: { id: 'positron', name: 'Positron', symbol: 'e⁺', color: '#50e0a0' },
  up: { id: 'antiup', name: 'Anti-up', symbol: 'ū', color: '#4a9eff' },
  top: { id: 'antitop', name: 'Anti-top', symbol: 't̄', color: '#4a9eff' },
  down: { id: 'antidown', name: 'Anti-down', symbol: 'd̄', color: '#4a9eff' },
};

export function findCollision(id1, id2) {
  return COLLISIONS.find(c =>
    (c.inputs[0] === id1 && c.inputs[1] === id2) ||
    (c.inputs[0] === id2 && c.inputs[1] === id1)
  );
}
